import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Copy, PackageCheck, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProductPicker } from "@/pages/admin/masterbills/ProductPicker";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const PURITIES = ["24K", "22K", "18K", "14K", "9K"];

const emptyRow = (productId = "", product = null) => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  product_id: productId,
  gross_weight_g: product?.gold?.gross_weight_g ?? "",
  net_weight_g: product?.gold?.weight_g ?? "",
  diamond_carat_total: product ? r3((product.diamonds || []).reduce((s, d) => s + (Number(d.carat) || 0), 0)) : "",
  purity: product?.gold?.karat || "",
  unit_cost: "",
  huid: "",
  stone_certificate_no: "",
  notes: "",
});

// Budget line: shows how much of the bill is still unassigned vs what this batch will use.
const Budget = ({ label, used, remaining, unit, format = (v) => v, testId }) => {
  const total = Math.max(0, remaining);
  const over = used > total + 0.0005;
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : (used > 0 ? 100 : 0);
  return (
    <div data-testid={testId} className={`rounded-lg border p-3 ${over ? "border-[#E7B9B9] bg-[#FBE7E7]" : "border-border bg-[#F7F0E6]"}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="uppercase text-muted-foreground">{label}</span>
        {over && <AlertTriangle className="h-3.5 w-3.5 text-[#7A1F1F]" />}
      </div>
      <div className={`font-mono text-sm mt-1 ${over ? "text-[#7A1F1F]" : ""}`}>
        {format(used)}{unit} <span className="text-muted-foreground">/ {format(total)}{unit} left</span>
      </div>
      <Progress value={pct} className="h-1.5 mt-2" />
    </div>
  );
};

// ItemizeDialog - turn part of a Master Bill's pending pool into physical pieces.
// Every row = ONE piece with its actual weight + carats. Live validation mirrors the
// backend: the batch can never exceed the bill's unassigned qty / gold / carats.
export function ItemizeDialog({ open, onOpenChange, bill, products, lockedProductId, onDone }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const lockedProduct = useMemo(() => products.find((p) => p.id === lockedProductId), [products, lockedProductId]);

  useEffect(() => {
    if (open) setRows([emptyRow(lockedProductId || "", lockedProduct || null)]);
    // eslint-disable-next-line
  }, [open, bill?.id, lockedProductId]);

  const remaining = {
    qty: Number(bill?.unassigned_qty) || 0,
    gold: Number(bill?.unassigned_gold_weight_g) || 0,
    ct: Number(bill?.unassigned_diamond_ct) || 0,
    inr: Number(bill?.unassigned_inr_cost) || 0,
  };
  const totals = rows.reduce((acc, r) => ({
    gold: acc.gold + (Number(r.gross_weight_g) || 0),
    ct: acc.ct + (Number(r.diamond_carat_total) || 0),
    inr: acc.inr + (Number(r.unit_cost) || autoCost(r)),
  }), { gold: 0, ct: 0, inr: 0 });

  function autoCost(r) {
    const g = Number(r.gross_weight_g) || 0;
    const totalW = Number(bill?.total_gold_weight_g) || 0;
    const totalCost = Number(bill?.total_inr_cost) || 0;
    const totalQ = Number(bill?.total_qty) || 0;
    if (totalW > 0 && g > 0) return Math.round((totalCost * g / totalW) * 100) / 100;
    if (totalQ > 0) return Math.round((totalCost / totalQ) * 100) / 100;
    return 0;
  }

  const trackGold = (Number(bill?.total_gold_weight_g) || 0) > 0;
  const trackCt = (Number(bill?.total_diamond_ct) || 0) > 0;
  const overQty = rows.length > remaining.qty;
  const overGold = trackGold && totals.gold > remaining.gold + 0.0005;
  const overCt = (trackCt && totals.ct > remaining.ct + 0.0005) || (!trackCt && totals.ct > 0.0005);
  const missingProduct = rows.some((r) => !r.product_id);
  const missingWeight = rows.some((r) => !(Number(r.gross_weight_g) > 0));
  const blocked = overQty || overGold || overCt || missingProduct || missingWeight || rows.length === 0;

  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const onProduct = (key, pid) => {
    const p = products.find((x) => x.id === pid);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...emptyRow(pid, p), key, notes: r.notes } : r)));
  };
  const onGross = (key, v) => setRows((rs) => rs.map((r) => {
    if (r.key !== key) return r;
    const g = Number(v) || 0;
    const ct = Number(r.diamond_carat_total) || 0;
    return { ...r, gross_weight_g: v, net_weight_g: g ? r3(Math.max(0, g - ct * 0.2)) : r.net_weight_g };
  }));
  const onCarat = (key, v) => setRows((rs) => rs.map((r) => {
    if (r.key !== key) return r;
    const g = Number(r.gross_weight_g) || 0;
    const ct = Number(v) || 0;
    return { ...r, diamond_carat_total: v, net_weight_g: g ? r3(Math.max(0, g - ct * 0.2)) : r.net_weight_g };
  }));
  const addRow = () => setRows((rs) => [...rs, emptyRow(lockedProductId || rs[rs.length - 1]?.product_id || "", products.find((p) => p.id === (lockedProductId || rs[rs.length - 1]?.product_id)) || null)]);
  const dupRow = (row) => setRows((rs) => { const i = rs.findIndex((r) => r.key === row.key); const copy = { ...row, key: emptyRow().key }; return [...rs.slice(0, i + 1), copy, ...rs.slice(i + 1)]; });
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const save = async () => {
    if (blocked || !bill) return;
    setSaving(true);
    try {
      const payload = { pieces: rows.map((r) => ({
        product_id: r.product_id,
        gross_weight_g: Number(r.gross_weight_g),
        net_weight_g: r.net_weight_g === "" ? null : Number(r.net_weight_g),
        diamond_carat_total: r.diamond_carat_total === "" ? 0 : Number(r.diamond_carat_total),
        purity: r.purity || "",
        unit_cost: r.unit_cost === "" ? null : Number(r.unit_cost),
        huid: (r.huid || "").trim(),
        stone_certificate_no: (r.stone_certificate_no || "").trim(),
        notes: r.notes || "",
      })) };
      const { data } = await api.post(`/inventory/master-bills/${bill.id}/itemize`, payload);
      toast.success(`${data.count} piece(s) itemized from ${bill.bill_no}`, { description: data.pieces.map((p) => p.barcode).slice(0, 4).join(", ") + (data.count > 4 ? " ..." : "") });
      onDone?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Itemization failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="itemize-dialog" className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><PackageCheck className="h-5 w-5 text-[hsl(var(--primary))]" /> Itemize Pieces</DialogTitle>
          <DialogDescription>
            {bill ? <>From <span className="font-mono font-medium">{bill.bill_no}</span> · {bill.source_name}{bill.reference_no ? <> · Ref <span className="font-mono">{bill.reference_no}</span></> : null}. Each row is one physical piece with its actual weight and carats; the cost is allocated from this bill and stays linked to the piece for life.</> : "Select a bill"}
          </DialogDescription>
        </DialogHeader>

        {bill && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Budget testId="budget-qty" label="Pieces" used={rows.length} remaining={remaining.qty} unit="" />
            <Budget testId="budget-gold" label="Gold weight" used={r3(totals.gold)} remaining={remaining.gold} unit=" g" format={(v) => r3(v).toFixed(3)} />
            <Budget testId="budget-diamond" label="Diamonds" used={r3(totals.ct)} remaining={remaining.ct} unit=" ct" format={(v) => r3(v).toFixed(2)} />
            <Budget testId="budget-inr" label="Cost allocated" used={totals.inr} remaining={remaining.inr} unit="" format={(v) => formatINR(v)} />
          </div>
        )}

        <Card className="p-3 sm:p-4 space-y-3">
          {rows.map((r, idx) => (
            <div key={r.key} data-testid={`itemize-row-${idx}`} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end rounded-lg border border-border/70 p-3 bg-white/60">
              <div className="col-span-2 md:col-span-3 min-w-0">
                <Label className="text-xs">Product Master (SKU) {idx === 0 && <span className="text-muted-foreground">· piece {idx + 1}</span>}</Label>
                <div className="mt-1"><ProductPicker products={products} value={r.product_id} onChange={(pid) => onProduct(r.key, pid)} disabled={!!lockedProductId} testId={`itemize-product-${idx}`} /></div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Gross Wt (g)</Label>
                <Input data-testid={`itemize-gross-${idx}`} type="number" step="0.001" min="0" value={r.gross_weight_g} onChange={(e) => onGross(r.key, e.target.value)} className="mt-1 font-mono h-10" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Net (g)</Label>
                <Input data-testid={`itemize-net-${idx}`} type="number" step="0.001" min="0" value={r.net_weight_g} onChange={(e) => setRow(r.key, { net_weight_g: e.target.value })} className="mt-1 font-mono h-10" />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs">Dia (ct)</Label>
                <Input data-testid={`itemize-carat-${idx}`} type="number" step="0.01" min="0" value={r.diamond_carat_total} onChange={(e) => onCarat(r.key, e.target.value)} className="mt-1 font-mono h-10" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Purity</Label>
                <Select value={r.purity || "none"} onValueChange={(v) => setRow(r.key, { purity: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid={`itemize-purity-${idx}`} className="mt-1 h-10"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{PURITIES.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Cost (INR) <span className="text-muted-foreground">auto {formatINR(autoCost(r))}</span></Label>
                <Input data-testid={`itemize-cost-${idx}`} type="number" step="1" min="0" value={r.unit_cost} onChange={(e) => setRow(r.key, { unit_cost: e.target.value })} placeholder={String(autoCost(r))} className="mt-1 font-mono h-10" />
              </div>
              <div className="col-span-1 md:col-span-2 min-w-0">
                <Input data-testid={`itemize-huid-${idx}`} value={r.huid} onChange={(e) => setRow(r.key, { huid: e.target.value.toUpperCase() })} placeholder="HUID (optional)" maxLength={12} className="h-9 text-xs font-mono uppercase" />
              </div>
              <div className="col-span-1 md:col-span-3 min-w-0">
                <Input data-testid={`itemize-certificate-${idx}`} value={r.stone_certificate_no} onChange={(e) => setRow(r.key, { stone_certificate_no: e.target.value })} placeholder="Stone certificate no. (GIA / IGI) - optional" className="h-9 text-xs font-mono" />
              </div>
              <div className="col-span-2 md:col-span-5 min-w-0">
                <Input data-testid={`itemize-notes-${idx}`} value={r.notes} onChange={(e) => setRow(r.key, { notes: e.target.value })} placeholder="Notes for this piece (tray, size, remarks) - optional" className="h-9 text-xs" />
              </div>
              <div className="col-span-2 md:col-span-2 flex items-center justify-end gap-1">
                <Button data-testid={`itemize-dup-${idx}`} type="button" size="icon" variant="ghost" className="h-9 w-9" title="Duplicate row" onClick={() => dupRow(r)}><Copy className="h-4 w-4" /></Button>
                <Button data-testid={`itemize-remove-${idx}`} type="button" size="icon" variant="ghost" className="h-9 w-9" title="Remove row" onClick={() => removeRow(r.key)} disabled={rows.length === 1}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button data-testid="itemize-add-row" type="button" variant="outline" size="sm" className="gap-1" onClick={addRow} disabled={rows.length >= remaining.qty}><Plus className="h-3.5 w-3.5" /> Add piece</Button>
            <div className="text-xs text-muted-foreground">
              {overQty && <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9] mr-2">Too many pieces for this bill</Badge>}
              {overGold && <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9] mr-2">Gold weight exceeds unassigned</Badge>}
              {overCt && <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9] mr-2">Diamond carats exceed unassigned</Badge>}
              {!blocked && `${rows.length} piece(s) ready · Net = Gross - (ct x 0.2) unless edited`}
            </div>
          </div>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="itemize-save-button" onClick={save} disabled={saving || blocked} className="gap-2"><PackageCheck className="h-4 w-4" /> {saving ? "Saving..." : `Itemize ${rows.length} piece${rows.length === 1 ? "" : "s"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
